import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import TableComponent from '../TableComponent';
import {
  createVariable,
  deleteVariable,
  getVariablesList,
  updateVariableValue,
} from '../../utils/api/Variable_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import VariablesTableFields from './TableField/Variables';
import AddUpdateVariablePopup from './Popup/AddUpdateVariablePopup';
import { Box } from '@mui/material';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../utils/uiText';

const TABLE_TITLE = 'All Variables';

export default function VariablesComponent() {
  const [loading, setLoading] = useState(false);
  const [variablesList, setVariablesList] = useState([]);
  const [popupType, setPopupType] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const { showSnackbar } = useSnackbar();

//   // Keep a ref to selectedRows for async actions
  const selectedRowsRef = useRef(selectedRows);
  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

    // Fetch variables on mount
    useEffect(() => {
        let isMounted = true;
        const fetchVariables = async () => {
            setLoading(true);
            try {
                const response = await getVariablesList();
                if (!isMounted) return;
                if (response.status === STATUS.SUCCESS) {
                    setVariablesList(response.data ?? []);
                    // showSnackbar(response.message || 'Variables list loaded.', 'success');
                } 
                // else {
                //     showSnackbar(response.error || 'Failed to fetch variables list.', 'error');
                // }
            } catch {
                if (isMounted) showSnackbar('Unexpected error fetching variables list.', 'error');
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchVariables();
        return () => {
            isMounted = false;
        };
    }, []);

    const togglePopup = useCallback((type, data = null) => {
        setPopupType(type);
        setPopupData(data);
    }, []);

    const closePopup = useCallback(() => {
        setPopupType(null);
        setPopupData(null);
    }, []);

    const handleVariableAdd = useCallback(
        async (data) => {
            setLoading(true);
            try {
                const response = await createVariable({
                    key: data?.key, 
                    value: data?.type === 'project_variable' ? data?.value : null, 
                    description: data?.description, 
                    projectVariable: data?.type === 'project_variable', 
                    isSecret: data?.isSecret, 
                });
                if (response.status === STATUS.SUCCESS) {
                    setVariablesList((prev) => [...prev, response.data]);
                closePopup();
                    showSnackbar(response.message || 'Variable created successfully.', 'success');
                } else {
                    showSnackbar(response.error || 'Failed to create variable.', 'error');
                }
            } finally {
                setLoading(false);
            }
        },
        [closePopup, showSnackbar]
    );

  const handleVariableUpdate = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await updateVariableValue({
          variable_id: data?.id,
          value: data?.value,
          description: data?.description,
          type: data?.type,
        });
        if (response.status === STATUS.SUCCESS) {          
          setVariablesList((vars) => {
            const updatedVars = [...vars];
            const updateIndex = updatedVars.findIndex((variable) => variable.id === response.data.id);
            updatedVars[updateIndex] = {
              ...updatedVars[updateIndex],
              value: response.data.value,
              description: response.data.description,
              updatedBy: response.data.updatedBy,
              updatedAt: response.data.updatedAt,
              }
            return [...updatedVars];
          });
          closePopup();
          showSnackbar(response.message || 'Variable updated successfully.', 'success');
        } else {
          showSnackbar(response.error || 'Failed to update variable.', 'error');
        }
      } finally {
        setLoading(false);
      }
    },
    [popupData, closePopup, showSnackbar]
  );

    const handleDeleteVariables = useCallback(
        async (ids = []) => {
            if (!ids.length) return;
            setLoading(true);
            try {
                const response = await deleteVariable({ variableIds: ids });
                if (response.status === STATUS.SUCCESS) {
                    setVariablesList((vars) => vars.filter((variable) => !ids.includes(variable.id)));
                    setSelectedRows([]);
                    closePopup();
                    showSnackbar(response.message || 'Variable deleted successfully.', 'success');
                } else {
                showSnackbar(response.error || 'Failed to delete variable.', 'error');
                }
            } finally {
                setLoading(false);
            }
        },
        [showSnackbar]
    );

  // Header action buttons
  const headerActionButtons = useMemo(
    () => [
      {
        name: 'Add',
        tooltip: 'Add New Variable',
        icon: <PlusIcon className='icon' />,
        action: () => togglePopup('add_variable'),
        // disabled: loading,
      },
      {
        name: 'Delete',
        tooltip: 'Delete Selected Variable(s)',
        icon: <TrashIcon className='icon'/>,
        action: () => togglePopup('delete_confirmation', selectedRowsRef.current),
        disabled: loading || !selectedRows.length,
      },
    ],
    [loading, togglePopup, handleDeleteVariables, selectedRows]
  );

  // Row action buttons
  const rowActionButtons = useMemo(
    () => [
      {
        name: 'Edit',
        tooltip: 'Edit',
        icon: <PencilIcon className='icon' />,
        action: (row) => togglePopup('edit_variable', row),
        disabled: false,
      },
      {
        name: 'Delete',
        tooltip: 'Delete',
        icon: <TrashIcon className='icon' />,
        action: (row) => togglePopup('delete_confirmation', [row.id]),
        disabled: false,
      },
    ],
    [togglePopup, handleDeleteVariables]
  );

    // Render popup based on type
    const RenderPopup = useCallback(() => {
        if (popupType === 'add_variable') {
          return (
              <AddUpdateVariablePopup
                  closePopup={closePopup}
                  handleVariableAdd={handleVariableAdd}
                  type="add"
                  loading={loading}
              />
          );
        }
        if (popupType === 'edit_variable') {
          return (
              <AddUpdateVariablePopup
                closePopup={closePopup}
                initialValues={{
                    id: popupData?.id,
                    key: popupData?.key,
                    description: popupData?.description,
                    isSecret: popupData?.encrypted,
                    type: popupData?.type,
                    value: popupData?.type === 'project_variable' ? popupData?.value : null,
                }}
                handleVariableUpdate={handleVariableUpdate}
                loading={loading}
                type="edit"
              />
          );
        }
        if (popupType === 'delete_confirmation') {
          return (
            <ConfirmationModal
              open={true}
              onClose={closePopup}
              onConfirm={() => handleDeleteVariables(popupData)}
              title={getDeleteModalTitle()}
              message={getDeleteConfirmMessage('variable', popupData?.length, popupData?.length > 1, 'Deleting a variable will permanently remove it, and test cases that use it will fail.')}
              loading={loading}
            />
          );
        }
        return null;
  }, [popupType, closePopup, handleVariableAdd, handleVariableUpdate, popupData, handleDeleteVariables, loading]);

  return (
    <>
      <TableComponent
        viewType="variables"
        title={TABLE_TITLE}
        headerActionButtons={headerActionButtons}
        rows={variablesList}
        columns={VariablesTableFields(rowActionButtons)}
        isCheckbox
        loading={loading}
        idField="id"
        isColumnResizable={!loading}
        isRowSelection
        handleRowSelection={setSelectedRows}
        selectedRows={selectedRows}
        initialState={{
          columns: {
            key: {
              visible: true,
            },
          },
        }}
      />
      {RenderPopup()}
    </>
  );
}